import {
  CompanySettlement,
  useCreateCompanySettlementMutation,
  useDeleteCompanySettlementMutation,
  useUpdateCompanySettlementMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseCompanySettlementMutationProps = {}

export type UseCompanySettlementMutationResult = {
  createCompanySettlement: (settlement: Partial<CompanySettlement>, onSuccess: (() => void) | undefined) => void
  updateCompanySettlement: (settlement: Partial<CompanySettlement>, onSuccess: (() => void) | undefined) => void
  deleteCompanySettlement: (id: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useCompanySettlementMutation = (props: UseCompanySettlementMutationProps = {}): UseCompanySettlementMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createCompanySettlement, {isLoading: isCreating}] = useCreateCompanySettlementMutation()
  const [_updateCompanySettlement, {isLoading: isUpdating}] = useUpdateCompanySettlementMutation()
  const [_deleteCompanySettlement, {isLoading: isDeleting}] = useDeleteCompanySettlementMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useCompanySettlementUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.settlement", "Company Settlement"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useCompanySettlementUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useCompanySettlementUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.settlement", "Company Settlement"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useCompanySettlementUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useCompanySettlementUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.settlement", "Company Settlement"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useCompanySettlementUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.settlement", "Company Settlement") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.settlement", "Company Settlement") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.settlement", "Company Settlement") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((id: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteCompanySettlement({id: id}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteCompanySettlement, handleDeleteResult])

  const createCompanySettlement = useCallback((settlement: Partial<CompanySettlement>, onSuccess: (() => void) | undefined) => {
    const promise = _createCompanySettlement(settlement).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createCompanySettlement, handleCreateResult])

  const updateCompanySettlement = useCallback((settlement: Partial<CompanySettlement>, onSuccess: (() => void) | undefined) => {
    const promise = _updateCompanySettlement(settlement).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateCompanySettlement, handleUpdateResult])

  const deleteCompanySettlement = useCallback((id: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.settlement", "Company Settlement"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.companysettlement.confirm", "Are you sure you want to delete this company settlement?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createCompanySettlement: createCompanySettlement,
    updateCompanySettlement: updateCompanySettlement,
    deleteCompanySettlement: deleteCompanySettlement,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
